import React from "react";
import { Link } from "react-router-dom";
import { useCompany } from "@/context/CompanyContext";
import { trackEvent } from "@/lib/ga";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqAccordion from "@/components/FaqAccordion";
import CtaBlock from "@/components/CtaBlock";
import LeadForm from "@/components/LeadForm";
import { FinancingBanner } from "@/components/FinancingBanner";
import { CreditCard, CheckCircle2, Phone, Percent, CalendarClock, ShieldCheck } from "lucide-react";

export default function Financing() {
  const { company } = useCompany();
  const faqs = [
    { q: "What can I finance?", a: "Financing is available on new AC and heat pump installations, complete system replacements, and larger repairs. Ask us what qualifies." },
    { q: "Will applying affect my credit?", a: "Application processes vary by lender. Many offer a quick pre-qualification. We'll walk you through the options during your estimate." },
    { q: "Are there low or no-interest options?", a: "We regularly offer promotional financing on qualifying installations. Ask about current offers when you request your estimate." },
  ];
  return (
    <>
      <Seo title="HVAC Financing in Cocoa, FL | Aquila Air & Heating" description="Flexible HVAC financing for new systems and major repairs across the Space Coast. Get the comfort you need now and pay over time with Aquila Air & Heating." path="/financing/" />
      <section className="bg-navy text-white py-12 md:py-16 dot-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Financing" }]} /></div>
          <p className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-launch"><CreditCard className="h-4 w-4" /> Comfort Now, Pay Over Time</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">HVAC Financing Options</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-200">Don't let a comfort emergency wait. We offer flexible financing so you can get the right high-efficiency system without draining your savings.</p>
          <a href={`tel:${company.phone_href || ""}`} onClick={() => trackEvent("financing_click", { location: "financing_hero" })} data-testid="financing-call" className="mt-7 inline-flex items-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3 px-7 rounded-md transition-colors"><Phone className="h-5 w-5" /> Ask About Financing</a>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-navy">Why Finance Your HVAC System?</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {[{ i: Percent, t: "Affordable Monthly Payments", d: "Spread the cost of a new system into manageable monthly payments." },
                { i: CalendarClock, t: "Get Comfortable Faster", d: "No need to wait and save — restore comfort now." },
                { i: ShieldCheck, t: "Invest in Efficiency", d: "A high-efficiency system lowers bills and can offset payments." },
                { i: CreditCard, t: "Simple Application", d: "Quick, straightforward process we help you through." }].map((b, i) => {
                const Icon = b.i;
                return (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`fin-benefit-${i}`}>
                    <Icon className="h-8 w-8 text-cool" />
                    <h3 className="mt-3 font-semibold text-navy">{b.t}</h3>
                    <p className="mt-1 text-sm text-slate-600">{b.d}</p>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 bg-slate-50 border border-slate-200 rounded-xl p-6">
              <h3 className="font-semibold text-navy">General Eligibility</h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                {["Available on qualifying new systems and major repairs", "Subject to credit approval through our financing partners", "Multiple term options to fit your budget", "Ask about current promotional offers"].map((t) => (
                  <li key={t} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-launch mt-0.5" /> {t}</li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-slate-500">Financing is provided by third-party lending partners and is subject to credit approval. Terms and offers may change. Contact us for current details.</p>
              <p className="mt-3 text-sm text-slate-600">Investing in a new system? Ask us about the <Link to="/lifetime-warranty" className="text-cool font-semibold hover:underline" data-testid="financing-warranty-link">Lifetime Warranty</Link> available for qualifying HVAC equipment.</p>
            </div>
          </div>
          <aside>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 lg:sticky lg:top-24">
              <h3 className="text-xl font-bold text-navy">Get Pre-Qualified</h3>
              <p className="text-sm text-slate-600 mt-1 mb-4">Tell us about your project and we'll review your options.</p>
              <LeadForm defaultService="Financing" compact />
              <FinancingBanner className="mt-6 pt-6 border-t border-slate-100" />
            </div>
          </aside>
        </div>
      </section>
      <FaqAccordion faqs={faqs} />
      <CtaBlock heading="Ready to Upgrade Your Comfort?" />
    </>
  );
}
